<template>
    <main id="pub" :class="[isDark ? 'dark' : '']">
        <div
            class="flex flex-col items-center justify-center overflow-x-hidden pb-10 pt-10 dark:bg-z-public-950"
        >
            <a
                v-if="!subscribed"
                target="_blank"
                class="mb-10 mt-10"
                :href="`${localisedFooter[locale].zaptime.link}?utm=from-event-type`"
            >
                <JetApplicationMark
                    class="dark:text-iron-100"
                ></JetApplicationMark>
            </a>

            <div
                v-if="isInOnboarding && !timeSlotHasBeenBooked"
                class="mb-4 font-medium"
            >
                <p class="inline-block dark:text-white">
                    {{ $t("onboarding.make_first_reservation") }}
                </p>

                <Button variant="link" as-child>
                    <Link
                        class="text-iron-500"
                        :href="route('app.event-types.index')"
                        >{{ $t("onboarding.skip_this_step") }}</Link
                    >
                </Button>
            </div>

            <div class="relative">
                <GradientBackground
                    v-if="!timeSlotHasBeenBooked && !subscribed"
                    class="absolute -right-1 -top-1 hidden opacity-20 blur-lg lg:block"
                ></GradientBackground>

                <GradientBackground
                    v-if="timeSlotHasBeenBooked && !subscribed"
                    class="absolute -top-10 right-[-75%] hidden opacity-10 blur-lg lg:block"
                ></GradientBackground>
                <div
                    class="relative rounded-[12px] shadow-lg ring-1 ring-z-public-200 dark:shadow-none dark:ring-z-public-700"
                >
                    <div
                        v-if="timeSlotHasBeenBooked && isInOnboarding"
                        class="mb-10 flex max-w-[400px] flex-col items-center gap-2 rounded-lg bg-white p-12"
                    >
                        <p class="text-2xl font-semibold">
                            {{ $t("onboarding.congratulations") }}!
                        </p>
                        <p
                            class="mb-4 max-w-xs text-center text-xl text-iron-700"
                        >
                            {{ $t("onboarding.success-1") }} <br />
                            {{ $t("onboarding.success-2") }}
                        </p>

                        <ThemedButton class="w-full">
                            <Link :href="route('app.dashboard')">{{
                                $t("onboarding.start-using-zaptime")
                            }}</Link>
                        </ThemedButton>
                    </div>

                    <ZaptimeCalendar
                        v-else
                        class=""
                        :config="config"
                        @booking-confirmed="onBookSuccess"
                    ></ZaptimeCalendar>
                </div>
            </div>

            <div
                class="relative mt-6 flex flex-col-reverse items-center justify-center gap-4 text-sm text-iron-500 transition-all duration-1000 ease-out dark:text-iron-200 sm:flex-row"
            >
                <a
                    class="block transition-all duration-200 ease-out hover:text-iron-400"
                    target="_blank"
                    :href="localisedFooter[locale]['privacy'].link"
                    >{{ localisedFooter[locale]["privacy"].text }}</a
                >

                <span class="hidden sm:block"> | </span>
                <a
                    class="block transition-all duration-200 ease-out hover:text-iron-400"
                    target="_blank"
                    :href="localisedFooter[locale]['terms'].link"
                    >{{ localisedFooter[locale]["terms"].text }}</a
                >
                <span v-if="!subscribed" class="hidden sm:block"> | </span>
                <a
                    v-if="!subscribed"
                    class="block transition-all duration-200 ease-out hover:text-iron-400"
                    :href="localisedFooter[locale]['zaptime'].link"
                >
                    {{ localisedFooter[locale]["zaptime"].text }}</a
                >
            </div>
        </div>
    </main>
</template>

<script lang="ts" setup>
import { ZaptimeCalendar } from "@zaptime/vue3";
import type { ZaptimeConfig } from "@zaptime/vue3";
import { ref } from "vue";
import GradientBackground from "../../../../Layouts/GradientBackgroud.vue";
import JetApplicationMark from "../../../../Jetstream/ApplicationMark.vue";
import { computed } from "vue";
import { useUrlSearchParams } from "@vueuse/core";
import { Link } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Button } from "@/components/ui/button";
import JSConfetti from "js-confetti";
import ThemedButton from "../../App/Atomic/ThemedButton.vue";
// import { useHead } from "@unhead/vue";

type ConfigWithoutToken = Omit<ZaptimeConfig, "token">;

type IProps = {
    eventType: IEventType;
    subscribed: boolean;
    reservationUuid?: string;
    name: string;
    duration?: number;
};

const props = defineProps<IProps>();

// useHead({
//     title: `${props.eventType.name} | ${props.name} | Zaptime`,
//     meta: [
//         {
//             property: "og:title",
//             content: `${props.eventType.name} | ${props.name} | Zaptime`,
//         },
//         {
//             property: "og:description",
//             content: `${props.eventType.name}`,
//         },
//         {
//             property: "og:url",
//             content: window.location.href,
//         },
//         {
//             property: "og:type",
//             content: "website",
//         },
//         {
//             property: "og:site_name",
//             content: "Zaptime.app",
//         },
//         {
//             property: "og:image",
//             content: encodeURI(
//                 `https://og-image-cloudflare.tomas-testing.workers.dev/?name=${props.name}&eventType=${props.eventType.name}&duration=${props.duration}&img=${encodeURIComponent(props.eventType.configuration.profileImage)}`,
//             ),
//         },
//     ],
// });

type IEventType = {
    uuid: string;
    name?: string;
    description?: string;
    token: string;
    configuration: ConfigWithoutToken;
};

const isInOnboarding = ref(false);
const timeSlotHasBeenBooked = ref(false);

const params = useUrlSearchParams<{
    onboarding?: string;
    reservationUuid?: string;
}>();

if (params.onboarding === "1") {
    isInOnboarding.value = true;
}

const locale =
    props.eventType.configuration.locale?.preset === "cs" ? "cs" : "en";

const localisedFooter = {
    en: {
        privacy: {
            text: "Privacy policy",
            link: "https://zaptime.app/privacy-policy",
        },
        terms: {
            text: "Terms of use",
            link: "https://zaptime.app/terms",
        },
        zaptime: {
            text: "Zaptime.app",
            link: "https://zaptime.app",
        },
    },

    cs: {
        privacy: {
            text: "Zásady ochrany osobních údajů",
            link: "https://zaptime.cz/privacy-policy",
        },
        terms: {
            text: "Obchodní podmínky",
            link: "https://zaptime.cz/terms",
        },
        zaptime: {
            text: "Zaptime.cz",
            link: "https://zaptime.cz",
        },
    },
} as const;

function onBookSuccess() {
    timeSlotHasBeenBooked.value = true;

    if (isInOnboarding.value) {
        const jsConfetti = new JSConfetti();
        jsConfetti.addConfetti();
    }
}

const isDark = computed(() => {
    return props.eventType.configuration.theme?.mode === "dark";
});

const theme = computed(() => {
    return props.eventType.configuration.theme;
});

const config: ZaptimeConfig = {
    token: props.eventType.token,
    reservationUuid: props.reservationUuid ?? params.reservationUuid,

    ...(import.meta.env.DEV && {
        apiBaseUrl: import.meta.env.VITE_API_URL,
    }),
};
</script>

<style scoped>
#pub {
    --pub-50: v-bind(theme?.colors?.[ "50"] || "#f8fafc");
    --pub-100: v-bind(theme?.colors?.[ "100"] || "#f1f5f9");
    --pub-200: v-bind(theme?.colors?.[ "200"] || "#e2e8f0");
    --pub-300: v-bind(theme?.colors?.[ "300"] || "#cbd5e1");
    --pub-400: v-bind(theme?.colors?.[ "400"] || "#64748b");
    --pub-500: v-bind(theme?.colors?.[ "500"] || "#475569");
    --pub-600: v-bind(theme?.colors?.[ "600"] || "#334155");
    --pub-700: v-bind(theme?.colors?.[ "700"] || "#384250");
    --pub-800: v-bind(theme?.colors?.[ "800"] || "#1e293b");
    --pub-900: v-bind(theme?.colors?.[ "900"] || "#020617");
    --pub-950: v-bind(theme?.colors?.[ "950"] || "#020617");
}
</style>
